import { useState } from 'react';
import { Input } from '../../components/ui';
import { useCompanies, useLeads } from '../tracker/hooks';
import { canSee, useMyPermissions } from '../auth/useMyPermissions';
import { useCreateDeal } from './hooks';
import { matchCompanies, matchLeads, writeForChoice } from './addDeal';
import { dealErrorMessage } from './queries';
import type { AddDealChoice, LeadLike } from './addDeal';
import type { Pipeline } from './types';
import './addDeal.css';

/**
 * The "+ Add deal" box, which now asks who the deal is for.
 *
 * Drawing only. Every rule is in addDeal.ts.
 *
 * ── The shape, and why it is not a dialog ──────────────────────────────────
 *
 * The old box was one inline field: type a name, press Enter, done. A required
 * "For" field on a modal would have made the fastest thing on the board into
 * the slowest, and Kyu uses this many times a day.
 *
 * So it stays one inline field. What changes is what you type INTO it: the
 * lead's name rather than the deal's. Pick from the list and the deal is
 * created, named after their company, attached to both them and the company.
 * For the normal case that is fewer keystrokes than before, not more.
 *
 * ── Somebody who cannot see Outbound ───────────────────────────────────────
 *
 * Deals and Sales Tracker are gated separately, so a person CAN hold Deals and
 * not Outbound. Nobody is in that state today — an earlier version of this
 * comment said two people were, which was wrong: the two it had in mind are on
 * Creatives and cannot open Deals at all. It is a shape the permissions allow,
 * not a group with members.
 *
 * For such a person the lead list does not exist and cannot be made to exist,
 * so the box falls back to the old plain-name field rather than presenting an
 * empty search that looks broken. What comes back is a refusal rather than a
 * deal: deals_must_be_attached rejects a deal with no lead and no company. So
 * the reason is printed under the field, and it is the one message in
 * DEAL_ERRORS that tells them what to ask for. Until that constraint existed
 * this branch had no error line, because nothing it did could fail.
 */
export function AddDealRow({
  pipeline,
  sectionId,
  position,
}: {
  pipeline: Pipeline;
  sectionId: string | null;
  position: number;
}) {
  const permissions = useMyPermissions();
  const canReachOutbound = canSee(permissions.data, 'sales_tracker');
  const leadsQuery = useLeads();
  const companiesQuery = useCompanies();
  const createDeal = useCreateDeal(pipeline);

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'lead' | 'company'>('lead');

  const leads: LeadLike[] = canReachOutbound ? (leadsQuery.data ?? []) : [];
  const companies = canReachOutbound ? (companiesQuery.data ?? []) : [];

  const choices = kind === 'lead' ? matchLeads(leads, query) : matchCompanies(companies, query);

  function pick(choice: AddDealChoice) {
    const write = writeForChoice(kind, choice, leads);
    createDeal.mutate({ ...write, sectionId, position }, { onSuccess: () => setQuery('') });
  }

  // No Outbound: the old box, unchanged. Nothing is gained by making somebody
  // search a list they are not allowed to read.
  if (!canReachOutbound) {
    return (
      <div className="deal-add-row">
        <Input
          label="New deal"
          labelHidden
          placeholder="+ Add deal"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || query.trim() === '') return;
            createDeal.mutate(
              { name: query.trim(), leadId: null, companyId: null, sectionId, position },
              { onSuccess: () => setQuery('') },
            );
          }}
        />
        {createDeal.error === null ? null : (
          <p className="add-deal-error" role="alert">
            {dealErrorMessage(String(createDeal.error.message ?? createDeal.error))}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="deal-add-row">
      <Input
        label={kind === 'lead' ? 'New deal — search a lead' : 'New deal — search a company'}
        labelHidden
        placeholder={kind === 'lead' ? '+ Add deal — type a lead' : '+ Add deal — type a company'}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          // Enter takes the top row. Typing "lazzara" and pressing Enter is the
          // whole interaction for the common case; the list is there for when
          // the top row is not the one you meant.
          if (event.key === 'Enter' && choices.length > 0) {
            event.preventDefault();
            pick(choices[0] as AddDealChoice);
          }
          if (event.key === 'Escape') {
            setQuery('');
          }
        }}
      />

      {query.trim() === '' ? null : (
        <div className="add-deal-choices">
          {choices.length === 0 ? (
            <p className="add-deal-empty">
              {kind === 'lead' ? 'No lead matches that.' : 'No company matches that.'}
            </p>
          ) : (
            choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="add-deal-choice"
                disabled={createDeal.isPending}
                onClick={() => pick(choice)}
              >
                <span className="add-deal-choice-name">{choice.label}</span>
                {/* Said before the click, not discovered after it. The deal is
                    named after the company, and that is not guessable from a
                    row that shows a person's name first. */}
                <span className="add-deal-choice-meta">deal: {choice.dealName}</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* The escape hatch, and it is a legitimate answer rather than a
          workaround — see addDeal.ts on why forcing everything through a lead
          would poison the Leads numbers. */}
      <button
        type="button"
        className="add-deal-switch"
        onClick={() => {
          setKind(kind === 'lead' ? 'company' : 'lead');
          setQuery('');
        }}
      >
        {kind === 'lead' ? 'Not a lead — pick a company instead' : 'Back to searching leads'}
      </button>
    </div>
  );
}
